/**
 * The dry-run endpoints.
 *
 * The client may not reimplement the validator, and the prototype's live validation is worth
 * keeping, so the editor asks the server on every edit. The server runs the identical check the
 * write path runs, which is what stops the editor disagreeing with the save (§6.3, backend §8.8).
 */

import { apiFetch } from './client';
import { routes } from '~/lib/routes';
import type * as T from './schema';

export async function validateRuleDraft(
  code: string,
  matter: string,
  lane: string,
  body: T.UpsertRuleRequest,
  signal?: AbortSignal,
): Promise<T.Validation> {
  const result = await apiFetch<T.Validation>(routes.api.validateRule(code, matter, lane), {
    method: 'POST',
    body,
    ...(signal ? { signal } : {}),
  });
  return result.data;
}

export async function validateLifecycleDraft(
  code: string,
  body: T.LifecycleValidateRequest,
  signal?: AbortSignal,
): Promise<T.Validation> {
  const result = await apiFetch<T.Validation>(routes.api.validateLifecycle(code), {
    method: 'POST',
    body,
    ...(signal ? { signal } : {}),
  });
  return result.data;
}

export async function validateSourceDraft(
  code: string,
  matter: string,
  body: T.AddSourceRequest,
  signal?: AbortSignal,
): Promise<T.Validation> {
  const result = await apiFetch<T.Validation>(routes.api.validateSource(code, matter), {
    method: 'POST',
    body,
    ...(signal ? { signal } : {}),
  });
  return result.data;
}

export async function validatePromptDraft(
  id: string,
  body: T.UpdatePromptRequest,
  signal?: AbortSignal,
): Promise<T.Validation> {
  const result = await apiFetch<T.Validation>(routes.api.validatePrompt(id), {
    method: 'POST',
    body,
    ...(signal ? { signal } : {}),
  });
  return result.data;
}

export async function previewPrompt(
  id: string,
  body: T.UpdatePromptRequest,
  signal?: AbortSignal,
): Promise<T.PromptPreview> {
  const result = await apiFetch<T.PromptPreview>(routes.api.previewPrompt(id), {
    method: 'POST',
    body,
    ...(signal ? { signal } : {}),
  });
  return result.data;
}

export async function calculate(
  code: string,
  matter: string,
  body: T.CalculationRequest,
  signal?: AbortSignal,
): Promise<T.Calculation> {
  const result = await apiFetch<T.Calculation>(routes.api.calculations(code, matter), {
    method: 'POST',
    body,
    ...(signal ? { signal } : {}),
  });
  return result.data;
}
